import { format } from 'date-fns';
import { AlertTriangle, ChartLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface OverviewTabProps {
  device: any;
  sensors: any[];
  sensorStats: any[];
  timeRange: string;
  setTimeRange: (range: string) => void;
  timeRangeOptions: { value: string; label: string }[];
  alertCount: number;
  setSelectedTab: (tab: string) => void;
}

export function OverviewTab({
  device,
  sensors,
  sensorStats,
  timeRange,
  setTimeRange,
  timeRangeOptions,
  alertCount,
  setSelectedTab,
}: OverviewTabProps) {
  // Get unique sensor types
  const sensorTypes = new Set(sensors.map((s) => s.type));

  // Get the selected time range option's label
  const selectedTimeRangeLabel =
    timeRangeOptions.find((option) => option.value === timeRange)?.label || 'Last 24 hours';

  return (
    <div className="space-y-6 pt-4">
      {/* Time range selector for overview */}
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Time range:</span>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Information */}
        <DeviceInfoCard device={device} />

        {/* Sensor Overview */}
        <SensorOverviewCard sensors={sensors} sensorTypes={sensorTypes} setSelectedTab={setSelectedTab} />

        {/* Latest Readings */}
        <LatestReadingsCard
          sensors={sensors}
          sensorStats={sensorStats}
          timeRange={timeRange}
          timeRangeLabel={selectedTimeRangeLabel}
          alertCount={alertCount}
          setSelectedTab={setSelectedTab}
        />
      </div>

      {/* Summary Statistics */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle>Summary Statistics ({selectedTimeRangeLabel})</CardTitle>
            <Button variant="ghost" onClick={() => setSelectedTab('analytics')}>
              <ChartLine className="h-4 w-4 mr-1" />
              View Analytics
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sensorTypes.size > 0 ? (
              Array.from(sensorTypes).map((type) => {
                // Find sensor stats for this type
                const typeSensors = sensorStats.filter((s) => {
                  const sensor = sensors.find((sensor) => sensor.id === s.sensor_id);
                  return sensor?.type === type;
                });

                // Get min, max, avg across all sensors of this type
                const values = typeSensors
                  .filter((s) => s.value !== null && s.value !== undefined)
                  .map((s) => Number(s.value));

                const min = values.length ? Math.min(...values).toFixed(1) : '-';
                const max = values.length ? Math.max(...values).toFixed(1) : '-';
                const avg = values.length ? (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(1) : '-';

                // Get unit from first sensor of this type
                const unit = sensors.find((s) => s.type === type)?.unit || '';

                return (
                  <Card key={type} className="bg-muted/30">
                    <CardHeader className="pb-1">
                      <CardTitle className="text-md">{type}</CardTitle>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-xs text-muted-foreground">Min</div>
                          <div className="font-medium">
                            {min} {unit}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Avg</div>
                          <div className="font-medium">
                            {avg} {unit}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Max</div>
                          <div className="font-medium">
                            {max} {unit}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <div className="col-span-3 text-center py-6">
                <p className="text-muted-foreground">No sensor data available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DeviceInfoCard({ device }: { device: any }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Device Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Device ID</p>
          <p className="text-sm font-mono">{device.id}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Serial Number</p>
          <p className="text-sm">{device.serial_number}</p>
        </div>
        {device.mac_address && (
          <div>
            <p className="text-sm font-medium text-muted-foreground">MAC Address</p>
            <p className="text-sm font-mono">{device.mac_address}</p>
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-muted-foreground">Description</p>
          <p className="text-sm">{device.description || 'No description'}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Created At</p>
          <p className="text-sm">{format(new Date(device.created_at), 'MMM d, yyyy HH:mm:ss')}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
          <p className="text-sm">{format(new Date(device.updated_at), 'MMM d, yyyy HH:mm:ss')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SensorOverviewCard({
  sensors,
  sensorTypes,
  setSelectedTab,
}: {
  sensors: any[];
  sensorTypes: Set<string>;
  setSelectedTab: (tab: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Sensor Overview</CardTitle>
      </CardHeader>
      <CardContent>
        {sensors.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No sensors connected to this device</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sensor Types</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {Array.from(sensorTypes).map((type) => (
                  <Badge key={type} variant="secondary">
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Sensors</p>
              <p className="text-xl font-medium">{sensors.length}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sensor Status</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded-md">
                  <p className="text-xs text-muted-foreground">Active</p>
                  <p className="text-lg font-medium">{sensors.filter((s) => s.latest_data).length}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-950/30 p-2 rounded-md">
                  <p className="text-xs text-muted-foreground">Inactive</p>
                  <p className="text-lg font-medium">{sensors.filter((s) => !s.latest_data).length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button variant="outline" className="w-full" onClick={() => setSelectedTab('sensors')}>
          View All Sensors
        </Button>
      </CardFooter>
    </Card>
  );
}

function LatestReadingsCard({
  sensors,
  sensorStats,
  timeRange,
  timeRangeLabel,
  alertCount,
  setSelectedTab,
}: {
  sensors: any[];
  sensorStats: any[];
  timeRange: string;
  timeRangeLabel: string;
  alertCount: number;
  setSelectedTab: (tab: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Latest Readings ({timeRangeLabel})</CardTitle>
      </CardHeader>
      <CardContent>
        {sensors.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No sensor data available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sensors.slice(0, 5).map((sensor) => {
              const sensorStat = sensorStats.find((s) => s.sensor_id === sensor.id);

              // Try to get the value from stats first, fall back to latest_data if needed
              let displayValue = 'No data';
              let displayUnit = '';

              if (sensorStat && sensorStat.value !== null && sensorStat.value !== undefined) {
                displayValue = Number(sensorStat.value).toFixed(1);
                displayUnit = sensor.unit || sensorStat.unit || '';
              } else if (sensor.latest_data && sensor.latest_data.value !== null) {
                displayValue = Number(sensor.latest_data.value).toFixed(1);
                displayUnit = sensor.unit || sensor.latest_data.unit || '';
              }

              // Similarly get timestamp from appropriate source
              const timestamp =
                sensorStat && sensorStat.timestamp
                  ? sensorStat.timestamp
                  : sensor.latest_data && sensor.latest_data.timestamp
                    ? sensor.latest_data.timestamp
                    : null;

              return (
                <div key={sensor.id} className="bg-muted/50 p-3 rounded-md">
                  <div className="flex justify-between">
                    <p className="font-medium">{sensor.name || sensor.type}</p>
                    {displayValue !== 'No data' ? (
                      <Badge variant="outline">
                        {displayValue} {displayUnit}
                      </Badge>
                    ) : (
                      <Badge variant="outline">No data</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {timestamp ? `Updated: ${format(new Date(timestamp), 'MMM d, HH:mm')}` : 'No data recorded'}
                  </p>
                </div>
              );
            })}
            {sensors.length > 5 && (
              <p className="text-xs text-center text-muted-foreground">+{sensors.length - 5} more sensors</p>
            )}
          </div>
        )}
      </CardContent>
      {alertCount > 0 && (
        <CardFooter>
          <Button variant="outline" className="w-full" onClick={() => setSelectedTab('alerts')}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            View {alertCount} Alert{alertCount !== 1 ? 's' : ''}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
